
import Rating, { RatingTypes } from '@/components/raytd/rating-tool';
import { convertRatingToIndex } from '@/app/features/reports/ratingHelpers';
import { RatingScale } from '@app.raytd.com/common/types/rating-scale'
import { cn } from '@/lib/utils';

interface RatingScaleDisplayProps {
    scale: RatingScale;
    className?: string;
    onClick?: (rating: string) => void;
    highlightedRating?: string;
}

function RatingScaleDisplay({ scale, className, onClick, highlightedRating = null }: RatingScaleDisplayProps) {

    const ratings = [
        { key: 'very_poor', label: scale.very_poor_label, description: scale.very_poor },
        { key: 'poor', label: scale.poor_label, description: scale.poor },
        { key: 'fair', label: scale.fair_label, description: scale.fair },
        { key: 'good', label: scale.good_label, description: scale.good },
        { key: 'very_good', label: scale.very_good_label, description: scale.very_good },
    ] as const;

    if (!scale.rateable) {
        return <NonRateableComponent />;
    }

    return (

        <div className={cn("rating-scale-grid grid grid-cols-5 gap-4 overflow-x-auto pb-2 min-h-72", className)}>
            {['very_poor', 'poor', 'fair', 'good', 'very_good'].map((key) => (
                <div
                    key={key}
                    onClick={() => onClick?.(key)}
                    className={cn('rating-scale-tile h-full border border-transparent', 
                        'transition-all duration-300',
                        onClick ? 'cursor-pointer hover:bg-gray-100' : '',
                        highlightedRating !== null && highlightedRating !== key ? 'opacity-50' : '',
                        highlightedRating === key ? 'border-zinc-950' : ''
                    )}

                >
                    {ratings.find(r => r.key === key) ? (
                        <RatingTile
                            key={key}
                            rating={key as 'very_poor' | 'poor' | 'fair' | 'good' | 'very_good'}
                            label={ratings.find(r => r.key === key)?.label || null}
                            description={ratings.find(r => r.key === key)?.description || null}
                            score={getRatingScore(key)}
                            type={scale.type}
                        />
                    ) : (
                        <div className="bg-gray-100 h-full rounded-lg"></div>
                    )}
                </div>
            ))}
        </div>
    )
}

function NonRateableComponent() {
    return (
        <div className="rating-scale-grid grid grid-cols-5 gap-4 overflow-x-auto pb-2 min-h-72">

            <div className="rating-scale-tile p-4 rounded bg-zinc-100 text-center h-full">
                <div className={`bg-zinc-100 rating-scale-container rounded-lg space-y-4 h-full flex flex-col`}>
                    <div className="rating-scale-header flex justify-between items-center">
                        <h5 className="rating-scale-title text-base font-medium capitalize">
                            Not Rated
                        </h5>
                        <div className="rating-scale-score text-gray-500 text-sm border-2 border-gray-200 rounded px-2 py-1">
                            0.00
                        </div>
                    </div>
                    <div className="rating-scale-stars">
                        <Rating type={'generic'} selected={null} width={240} height={40} />
                    </div>
                    <div className="rating-scale-description space-y-2 text-sm flex-grow whitespace-pre-wrap">

                    </div>
                </div>
            </div>
            <div className="col-span-1"></div>
        </div>
    );
}

function EmptyTile() {
    return (
        <div className="bg-white border border-zinc-200 h-full rounded-lg"></div>
    );
}


function RatingTile({
    rating,
    label,
    description,
    score,
    type
}: {
    rating: 'very_poor' | 'poor' | 'fair' | 'good' | 'very_good';
    label: string | null;
    description: string | null;
    score: number;
    type: RatingTypes;
}) {
    if (!description) return <EmptyTile />;

    const getBgColor = () => {
        switch (type) {
            case 'compliance':
            case 'compliance_b':
                return 'bg-green-50';
            case 'condition':
                return 'bg-blue-50';
            case 'generic':
                return 'bg-zinc-50';
            default:
                return 'bg-white';
        }
    };

    return (
        <div className={`${getBgColor()} p-4 rating-scale-container rounded-lg space-y-4 h-full flex flex-col`}>
            <div className="rating-scale-header flex justify-between items-center print:flex-col print:gap-2">
                <h5 className="rating-scale-title text-base font-medium capitalize">
                    {label || rating.split('_').join(' ')}
                </h5>
                <div className="rating-scale-score text-gray-500 text-sm border-2 border-gray-200 rounded px-2 py-1">
                    {score.toFixed(2)}
                </div>
            </div>
            <div className="rating-scale-stars">
                <Rating type={type} selected={convertRatingToIndex(rating)} width={240} height={40} />
            </div>
            <div className="rating-scale-description space-y-2 text-sm flex-grow whitespace-pre-wrap print:text-sm/4">

                {description.replace(/\\n/g, '\n').split('\n').map((str, index) => (
                    <p key={index}>{str}</p>
                ))}

            </div>
        </div>
    );
}

const getRatingScore = (rating: string): number => {
    const scores: Record<string, number> = {
        'very_poor': 1,
        'poor': 2,
        'fair': 3,
        'good': 4,
        'very_good': 5
    };
    return scores[rating] || 0;
};

export default RatingScaleDisplay;